import * as Sentry from '@sentry/react';
import { redactSecretUrl, redactSecretValue } from '@/utils/redactSecretPath';

/**
 * Scrub every URL-ish field on a Sentry payload of single-use tokens / raw-email
 * segments — including query params (`?invite=<token>`) and fragments (round-8d
 * #3 + round-9 #4, §4.3). `browserTracingIntegration` + `replayIntegration`
 * capture the live location into `event.request.url`, `event.transaction`,
 * navigation breadcrumbs, and replay recordings — any of which can carry a
 * `/connect/respond/:token`, `/owner/claims/verify/:token`, `/invite/:token`,
 * `/unsubscribe/:token`, `/suppressions/:email`, or a `?invite=<token>` query.
 * `redactSecretUrl` is the single source of truth for scrubbing a full URL
 * (path + query values + fragment).
 *
 * Runs from BOTH `beforeSend` (error events) AND `beforeSendTransaction`
 * (performance/transaction events) — the SDK invokes these on DISJOINT event
 * pipelines, so scrubbing only `beforeSend` (round-9 #4c / adversarial NEW-1)
 * left sampled transactions shipping the raw token in `event.transaction` +
 * `event.request.url`. Typed loosely (`ErrorEvent | TransactionEvent` share the
 * fields we touch) so one function covers both channels.
 */
function scrubEventUrls<T extends Sentry.ErrorEvent | Sentry.TransactionEvent>(event: T): T {
  if (event.request?.url) {
    event.request.url = redactSecretUrl(event.request.url);
  }
  if (typeof event.transaction === 'string') {
    event.transaction = redactSecretUrl(event.transaction);
  }
  if (Array.isArray(event.breadcrumbs)) {
    for (const crumb of event.breadcrumbs) {
      scrubBreadcrumb(crumb);
    }
  }
  // round-10 #8: a transaction event carries its CHILD spans under `event.spans`. Each
  // `http.client` child (a Connect peek/respond fetch, a claim-invite verify, a signed-GCS
  // PUT) names itself by the raw URL (`description`) and stashes the URL/query/fragment in
  // its `data` attributes — none of which `event.request`/`event.transaction` covers. The
  // `beforeSendSpan` hook (below) scrubs each span as it is serialised, but that hook does
  // NOT fire for spans already materialised onto a transaction event in some SDK paths, so
  // we ALSO traverse `event.spans` defensively here — belt-and-braces so a child span can
  // never ship a raw token/PII URL regardless of which pipeline built the event.
  const spans = (event as Sentry.TransactionEvent).spans;
  if (Array.isArray(spans)) {
    for (const span of spans) {
      scrubSpan(span);
    }
  }
  // round-11 F2: an error's free-text message / exception value can embed a token-bearing
  // URL or contact PII (a thrown fetch error whose message is the signed-GCS PUT url, an
  // upstream error string carrying `?invite=<token>`). The backend Sentry config scrubs the
  // whole exception tree; the FE only covered request/transaction/breadcrumb/span. Mirror it.
  const errorEvent = event as Sentry.ErrorEvent;
  if (typeof errorEvent.message === 'string') {
    errorEvent.message = redactSecretValue(errorEvent.message) as string;
  }
  if (Array.isArray(errorEvent.exception?.values)) {
    for (const ex of errorEvent.exception.values) {
      if (typeof ex.value === 'string') {
        ex.value = redactSecretValue(ex.value) as string;
      }
    }
  }
  return event;
}

/**
 * Span attribute keys whose VALUE is (or contains) a full URL / query / fragment that a
 * telemetry sink must not persist raw. Sentry's fetch/xhr instrumentation writes the request
 * URL under `url` + `http.url`, the query under `http.query`, the fragment under
 * `http.fragment`, and stable-semantic equivalents under `url.full` / `url.path` / `http.target`
 * / `http.route`. Each is run through the full-URL scrubber (idempotent on benign values).
 */
const SPAN_URL_ATTRIBUTE_KEYS: readonly string[] = [
  'url',
  'http.url',
  'url.full',
  'url.path',
  'http.target',
  'http.route',
  'http.query',
  'http.fragment',
];

/**
 * Scrub a single serialised span (`SpanJSON`) of token/PII URL data (round-10 #8). A
 * BrowserTracing `http.client` child span for a Connect peek/respond, a claim-invite verify,
 * or a signed-GCS PUT names itself `"<METHOD> <raw-url>"` (`description`) and repeats the URL /
 * query / fragment across its `data` attributes. `redactSecretUrl` strips the path token, the
 * sensitive query VALUES, and the fragment from each. Mutates in place (the SDK hands us the
 * outgoing object) and returns it so it composes as a `beforeSendSpan` hook.
 */
function scrubSpan(span: Sentry.SpanJSON): Sentry.SpanJSON {
  if (typeof span.description === 'string') {
    span.description = redactSecretUrl(span.description);
  }
  const data = span.data as Record<string, unknown> | undefined;
  if (data && typeof data === 'object') {
    for (const key of SPAN_URL_ATTRIBUTE_KEYS) {
      const val = data[key];
      if (typeof val === 'string') {
        data[key] = redactSecretUrl(val);
      }
    }
  }
  return span;
}

/**
 * Scrub token/PII path/query/fragment segments out of a single breadcrumb.
 * Navigation and fetch/xhr breadcrumbs stash the URL under `data.url` / `data.to`
 * / `data.from`; the human-readable `message` can also embed the path. Runs both
 * from `beforeBreadcrumb` (as crumbs are recorded, so Replay's copy is clean too)
 * and from `beforeSend` (defense-in-depth for any crumb already on the event).
 */
function scrubBreadcrumb(crumb: Sentry.Breadcrumb): Sentry.Breadcrumb {
  if (typeof crumb.message === 'string') {
    crumb.message = redactSecretUrl(crumb.message);
  }
  const data = crumb.data;
  if (data && typeof data === 'object') {
    for (const key of ['url', 'to', 'from'] as const) {
      const val = (data as Record<string, unknown>)[key];
      if (typeof val === 'string') {
        (data as Record<string, unknown>)[key] = redactSecretUrl(val);
      }
    }
  }
  return crumb;
}

/**
 * Scrub URL/PII out of a Replay RECORDING event before it is written into the recording
 * (round-10 #9). Replay records its own navigation/history/slow-click frames whose `data`
 * carries the raw URL (`data.url`, `data.to`, `data.from`, `data.href`) — a DISTINCT sink
 * from Sentry breadcrumbs (which `beforeBreadcrumb` covers) and from rendered text/inputs
 * (which `maskAllText`/`maskAllInputs` cover). Landing on a token route, an invite query, or
 * a signed-GCS request otherwise persists the raw token/PII into the replay recording. This
 * runs as the Replay `beforeAddRecordingEvent` hook. Typed loosely (the frame shape is a
 * `ReplayFrameEvent` whose `data` is an open record) and guarded so it can never throw inside
 * the recording pipeline. Returns the (mutated) frame — never `null`, so the frame is kept,
 * only scrubbed.
 */
function scrubRecordingEvent(event: unknown): unknown {
  if (!event || typeof event !== 'object') return event;
  // The frame payload lives under `data` (and, for custom breadcrumb frames, `data.payload`).
  const data = (event as { data?: unknown }).data;
  scrubUrlBearingRecord(data);
  if (data && typeof data === 'object') {
    scrubUrlBearingRecord((data as { payload?: unknown }).payload);
    // Nested breadcrumb-style frame: `data.payload.data.{url,to,from}`.
    const payload = (data as { payload?: unknown }).payload;
    if (payload && typeof payload === 'object') {
      scrubUrlBearingRecord((payload as { data?: unknown }).data);
    }
  }
  return event;
}

/** Scrub the URL-bearing string fields of an arbitrary record in place (defensive/no-throw). */
function scrubUrlBearingRecord(rec: unknown): void {
  if (!rec || typeof rec !== 'object') return;
  const obj = rec as Record<string, unknown>;
  for (const key of ['url', 'to', 'from', 'href', 'name'] as const) {
    const val = obj[key];
    if (typeof val === 'string') {
      obj[key] = redactSecretUrl(val);
    }
  }
}

/**
 * Event processor that scrubs a Replay ENVELOPE event's `urls` array (round-10 #9). Sentry
 * stores the visited URLs in TWO places — the recording events (scrubbed above) AND the
 * `replay_event.urls` list on the flushed envelope — and `beforeSend` does NOT run for
 * `replay_event`s, so neither the error nor the transaction scrub reaches this list. Registered
 * as a global event processor (which `prepareReplayEvent` DOES run), it redacts every entry so a
 * token-bearing route the user visited never ships as a raw replay URL. `urls` is not on the
 * public `Event` type, so it is accessed via a narrow cast.
 */
function scrubReplayEventUrls(event: Sentry.Event): Sentry.Event {
  if (event.type !== 'replay_event') return event;
  const withUrls = event as Sentry.Event & { urls?: unknown };
  if (Array.isArray(withUrls.urls)) {
    withUrls.urls = withUrls.urls.map((u) =>
      typeof u === 'string' ? redactSecretUrl(u) : u,
    );
  }
  return event;
}

export const initSentry = () => {
  // Only initialize if DSN is configured
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  if (!dsn) {
    // Sentry not configured
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,

    // §4.3: never let the SDK auto-attach PII (IP address, cookies, user headers).
    // We attach only the fields we control, all of which are scrubbed below.
    sendDefaultPii: false,

    // Performance monitoring
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Mask all rendered text + inputs so investor/LP financial fields and
        // contact details are never captured verbatim into a replay.
        maskAllText: true,
        maskAllInputs: true,
        // round-10 #9: scrub the URL/PII out of each Replay RECORDING event (navigation /
        // history / slow-click frames stash the raw URL under `data.url`/`to`/`from`) — a
        // sink that text/input masking and `beforeBreadcrumb` do NOT cover. The envelope's
        // `replay_event.urls` list is scrubbed separately via the event processor below.
        beforeAddRecordingEvent: (event) =>
          scrubRecordingEvent(event) as ReturnType<
            NonNullable<Parameters<typeof Sentry.replayIntegration>[0]>['beforeAddRecordingEvent']
          >,
      }),
    ],

    // Sample rates
    tracesSampleRate: 0.1, // 10% of transactions
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of errors

    // Scrub token/PII path segments out of every breadcrumb as it is recorded.
    // Doing it HERE (not only in beforeSend) means the Replay recording — which
    // consumes the same navigation breadcrumbs — never sees the raw token either.
    beforeBreadcrumb(breadcrumb) {
      return scrubBreadcrumb(breadcrumb);
    },

    // Filter out expected errors + scrub token/PII from the outgoing event.
    beforeSend(event, hint) {
      const error = hint.originalException;

      // Don't send auth errors (expected)
      if (error instanceof Error) {
        if (error.message.includes('401') || error.message.includes('403')) {
          return null;
        }
      }

      return scrubEventUrls(event);
    },

    // Scrub token/PII from PERFORMANCE (transaction) events too. `beforeSend`
    // only fires for ERROR events; `browserTracingIntegration` names each
    // transaction from the raw pathname and ships it on a SEPARATE pipeline
    // (round-9 #4c / adversarial NEW-1). Without this, ~10% of pageloads
    // (tracesSampleRate) on `/connect/respond/:token`, `/owner/claims/verify/:token`,
    // `/invite/:token`, `?invite=<token>` etc. leaked the live token into the
    // transaction name + request.url. Scrub it here on that pipeline too.
    beforeSendTransaction(event) {
      return scrubEventUrls(event);
    },

    // round-10 #8: scrub every ROOT and CHILD span before it ships. `beforeSendSpan` is the
    // Sentry hook applied to the transaction's segment span AND each of its child spans on the
    // span pipeline — the one place a `http.client` child (Connect peek/respond, claim-invite
    // verify, signed-GCS PUT) can be caught before its raw URL `description` + `data` attributes
    // reach Sentry. `beforeSendTransaction` only scrubs the root event fields; the child spans
    // ride a distinct pipeline, exactly as the error/transaction split did (round-9 #4c).
    beforeSendSpan(span) {
      return scrubSpan(span);
    },

    // Ignore certain errors
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'Non-Error promise rejection captured',
    ],
  });

  // round-10 #9: scrub the Replay ENVELOPE's `urls` list. `beforeSend` does NOT run for
  // `replay_event`s, but the global event processors DO (via `prepareReplayEvent`), so this is
  // the correct hook to redact the visited-URL list a replay ships (distinct from the recording
  // events scrubbed by `beforeAddRecordingEvent` above).
  Sentry.addEventProcessor(scrubReplayEventUrls);
};
